import type { ImageId } from '../../assets/marketing/manifest';

/**
 * Home page copy. Everything a copywriter would change lives here; the
 * sections take these as props. Lines marked from the guidelines are the
 * studio's own (brand/GUIDELINES.md §7–8); the rest carries over from the
 * previous home page or is a placeholder for the copy pass.
 */

export const HOME_META = {
  title: 'Melio · Your health, managed.',
  description:
    'Melio brings every part of your health together under one expert. Testing, appointments, specialists, records and long-term planning, held by your Head of Health.',
} as const;

/** Guidelines p.53, verbatim. */
export const HOME_INTRO = {
  statement:
    'At Melio, we believe your health should be understood as a whole. One trusted relationship, bringing everything together so you can focus on living well.',
  link: { label: 'More about us', to: '/about' },
} as const;

export interface Step {
  id: string;
  numeral: string;
  title: string;
  body: string;
}

/** "How it works" — the owner's copy for the membership page (2026-09-15); the home page reuses it. */
export const HOW_IT_WORKS_STEPS: Step[] = [
  {
    id: 'biomarker-context',
    numeral: 'I',
    title: 'Biomarker context',
    body:
      'A lab-direct saliva genetics kit and a bespoke Melio bloods panel. We treat these as context for the wider story.',
  },
  {
    id: 'clinical-history',
    numeral: 'II',
    title: 'Clinical history',
    body:
      'You sit with a clinician for a guided personal and family-history review. Structured intake, written down once and re-read each quarter.',
  },
  {
    id: 'health-review',
    numeral: 'III',
    title: 'A first review',
    body:
      'Your clinician writes a surveillance plan and walks you through it. A clear view of what matters now and what can wait.',
  },
  {
    id: 'continued-surveillance',
    numeral: 'IV',
    title: 'Continued care',
    body: 'Quarterly reports, monitoring and course-correction. Year-round, by the same small team.',
  },
];

export interface StripItem {
  id: string;
  image: ImageId;
  /** Details-caps caption above the paragraph, e.g. "PROACTIVE, NOT REACTIVE". */
  caption: string;
  body: string;
}

/**
 * The four-up image strip under "WHY MELIO?". Four parallel single-word
 * headings and the owner's copy (2026-09-15). Public prose rules: closed em
 * dashes, no Oxford comma.
 */
export const WHY_MELIO_STRIP: StripItem[] = [
  {
    id: 'targeted',
    image: 'strip-01',
    caption: 'Targeted',
    body:
      'Your genetics, history and goals shape what we test, what we watch and what we revisit over time—not a fixed panel sold to everyone.',
  },
  {
    id: 'known',
    image: 'strip-02',
    caption: 'Known',
    body:
      'A named clinician leads your care and follows your progress over time. If they are unavailable, another clinician from the same small team can step in with the full context.',
  },
  {
    id: 'proactive',
    image: 'strip-03',
    caption: 'Proactive',
    body: 'We look beyond today. Patterns over time help us anticipate what may matter and act earlier.',
  },
  {
    id: 'continuous',
    image: 'strip-04',
    caption: 'Continuous',
    body:
      'A bespoke schedule only works if someone stays with you. Melio is designed for course correction across years and evolves with you.',
  },
];

/** The intro and the pillar list stay unnumbered; the numbered chapters start here (owner, 2026-09-15). */
export const HOME_SECTIONS = {
  why: { numeral: 'I', title: 'Why Melio?' },
  how: { numeral: 'II', title: 'How it works' },
} as const;

export const PILLARS_LABEL = 'Your health, managed.';

/** The band that opens the Health Check panel (guidelines p.55). */
export const HEALTH_CHECK_BAND = {
  label: 'Melio Health Check',
  title: 'How well is your health being managed?',
  body: 'A few questions to understand where you are today.',
  cta: 'Start your health check',
} as const;
